import '../styles.css';
import { storageAssets } from '../storage_assets';
import IterationStatus from '../components/iterationStatus';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import { ArrowIcon } from '../icons/navigationIcons';
import { openExternal } from '../utils/openExternal';

/* Typography: see styles.css — .text-header-main, .text-body, .text-body-sm */

const ProjectOnePage = () => {
    return (
        <div className="scrollable-window scrollable-window--wide">
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader
                        title="The Madder controller"
                        action={
                            <Button
                                variant="text"
                                text="Visit Madder"
                                icon={<ArrowIcon />}
                                iconPosition="end"
                                onClick={() => openExternal('https://maddergames.com/about')}
                            />
                        }
                    />
                    <div className="text-body-sm">
                        As a co-founder at Madder, I developed the Madder mobile controller app, which gained 2,000+ downloads on
                        the App Store. The app was designed for game makers who needed a ready-made controller for the Madder
                        console. The initial version was quickly built, and a more refined version followed to address usability
                        issues. I led the UX research, collaborated with the team, and handled all aesthetic design.
                    </div>
                    <div style={styles.deviderStroke}></div>

                    <div className="text-body">The initial controller</div>
                    <div className="text-body-sm">
                        The initial controller focused on two main usability goals: easy-to-press buttons and versatility. Large
                        buttons made it easier for users to press without looking, as Madder requires the user’s vision elsewhere.
                        The controller’s versatility was ensured by using distinct shapes for each button, allowing it to accommodate
                        a wide range of games.
                    </div>
                    <img src={storageAssets.initialController} alt="Initial controller layout" style={styles.image} />
                    <div className="text-body">Pinpointing problems</div>
                    <div className="text-body-sm">
                        Through careful observation, I noticed that users with smaller thumbs seemed to have a harder time reaching
                        the far side of the joystick and the right-side buttons.
                    </div>
                    <img src={storageAssets.problemOne} alt="Thumb reach on the initial controller" style={styles.image} />
                    <div className="text-body-sm">
                        Additionally, I found that when users first pressed the joystick, they were often not perfectly centered on
                        it, which caused their game character to move unintentionally.
                    </div>
                    <img src={storageAssets.problemTwo} alt="Off-center joystick press" style={styles.image} />
                    <div className="text-body">Iteration #1</div>
                    <div className="text-body-sm">
                        Using the “how might we…” (HMW) question format, I started iterating on ideas for an effective solution. The
                        first question I asked myself was: HMW help users with smaller thumbs use the joystick easily? To accommodate
                        smaller thumbs, I created a smaller joystick and enlarged the buttons on the right side.
                    </div>
                    <img src={storageAssets.iterationOne} alt="First iteration of the controller" style={styles.image} />
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'The joystick is smaller and placed at the corner of the screen—improving reachability.',
                            },
                            {
                                type: 'fail',
                                text: 'The smaller joystick may be harder to press when not looking.',
                            },
                            {
                                type: 'fail',
                                text: 'The user may not land their thumb perfectly centered on the joystick—which can result in unwanted movement.',
                            },
                        ]}
                    />
                    <div className="text-body">Iteration #2</div>
                    <div className="text-body-sm">
                        I asked: HMW help all users quickly place their left thumb on the joystick without looking down? The
                        solution is a dynamic joystick that only renders when pressed. If the user presses outside the pressing zone,
                        it renders off-center.
                    </div>
                    <img src={storageAssets.iterationTwo} alt="Second iteration with dynamic joystick" style={styles.image} />
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'The joystick appears only when pressed. It centers itself wherever the initial press is (if pressed within the pressing zone). Users can use it effectively without looking at it.',
                            },
                            {
                                type: 'success',
                                text: 'An even smaller joystick allows for a larger pressing zone—fit for most users and screen sizes.',
                            },
                            {
                                type: 'fail',
                                text: 'New users might not understand this new and unconventional usage.',
                            },
                        ]}
                    />
                    <div className="text-body">Final iteration</div>
                    <div className="text-body-sm">
                        Guided by the question: HMW help users quickly recognize the new joystick’s functionality?, I created a
                        signifier that cues users about how the new joystick works.
                    </div>
                    <img src={storageAssets.finalIteration} alt="Final controller with joystick signifier" style={styles.video} />
                    <IterationStatus
                        items={[
                            {
                                type: 'success',
                                text: 'The joystick appears on press and centers itself wherever the initial press is (as long as the initial press is within the pressing zone). That allows pressing without looking.',
                            },
                            {
                                type: 'success',
                                text: 'The small joystick allows for a large pressing zone—facilitating an easy pressing experience for most users and screen sizes.',
                            },
                            {
                                type: 'success',
                                text: 'An icon acts as a signifier for the joystick area. That cues the user’s mental model of its functionality and improves discoverability.',
                            },
                        ]}
                    />
                    <div className="text-body">Additional specs</div>
                    <div className="text-body-sm">
                        All buttons are 3D modeled with both pressed and unpressed versions to fit the user’s mental model of
                        buttons; other elements, like the joystick signifier, contrast by feeling deliberately flat and
                        non-pressable. Light haptics fire when the joystick renders or when any button is pressed. That adds a
                        secondary modality to reinforce perception and further reduce reliance on sight when operating the
                        controller.
                    </div>
                    <img src={storageAssets.additionalSpecs} alt="Additional controller specifications" style={styles.image} />
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        maxWidth: '100%',
        minWidth: 0,
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    image: {
        width: 'var(--content-media-width)',
        height: 'auto',
        maxWidth: '100%',
    },
    video: {
        width: 'var(--content-media-width)',
        height: 'auto',
        maxWidth: '100%',
        borderRadius: 'clamp(1rem, 2vw, 1.5625rem)',
    },
    deviderStroke: {
        height: '1px',
        backgroundColor: 'var(--color-border)',
        alignSelf: 'stretch',
    },
};

export default ProjectOnePage;
